import {
  OreCheckbox,
  OreFormGroup,
  OreHeading,
  OreInput,
  OreSelect,
  OreSelectPlaceholder,
  OreStack,
} from '@edenredespana/oreneta'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { handleErrors } from '@/Shared/ui/form'
import { FoodTypeModel } from '@/Store/domain'
import { StoreForm } from '@/Store/ui/hooks'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { MerchantsDataModel } from '@/User/domain'

interface MyStoreBasicFormRestaurantProps {
  foodTypes: FoodTypeModel[]
  show: boolean
  merchant: MerchantsDataModel
}

export const MyStoreBasicFormRestaurant = ({
  foodTypes,
  show,
  merchant,
}: MyStoreBasicFormRestaurantProps): JSX.Element => {
  const { t } = useTranslation()
  const { register, formState, resetField, getValues, setError, clearErrors } =
    useFormContext<StoreForm>()
  const { errors } = handleErrors(formState)

  const { onChange: onChangeOnline, ...restOnline } = register('merchantIsOnlineStore')
  const { onChange: onChangeDelivery, ...restDelivery } = register(
    'merchantHasHomeDelivery',
  )
  const { onChange: onChangeTakeAway, ...restTakeAway } = register(
    'merchantHasLocalPickup',
  )

  const serviceError = (): void => {
    if (
      !getValues('merchantIsOnlineStore') &&
      !getValues('merchantHasHomeDelivery') &&
      !getValues('merchantHasLocalPickup')
    ) {
      setError('service', {
        type: 'manual',
        message: t(storesTranslation.myStore.form.data.service.noServiceError),
      })
    } else {
      clearErrors('service')
    }
  }

  useEffect(() => {
    resetField('merchantCoockTypeId')
  }, [foodTypes])

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(storesTranslation.myStore.form.data.title)}
      </OreHeading>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('merchantName')}
          {...errors('merchantName')}
          id="name"
          label={merchant.isNursery ? 
            t(storesTranslation.myStore.form.data.name.labelGuarderia) : 
            t(storesTranslation.myStore.form.data.name.label)}
          placeholder={t(storesTranslation.myStore.form.data.name.placeholder)}
          labelTooltip={merchant.isNursery ? undefined : 
            t(storesTranslation.myStore.form.data.name.tooltip)}
          required
          maxLength={255}
          disabled={!show}
        />
        <OreInput
          {...register('afiliateCode')}
          {...errors('afiliateCode')}
          id="afiliateCode"
          label={t(storesTranslation.myStore.form.data.afiliateCode.label)}
          placeholder={t(storesTranslation.myStore.form.data.afiliateCode.placeholder)}
          required
          disabled={true}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('addressEmail')}
          {...errors('addressEmail')}
          id="email"
          label={t(storesTranslation.myStore.form.data.email.label)}
          type="email"
          placeholder={t(storesTranslation.myStore.form.data.email.placeholder)}
          required
          maxLength={255}
          disabled={!show}
        />
        <OreInput
          {...register('storePhone')}
          {...errors('storePhone')}
          id="storePhone"
          label={t(storesTranslation.myStore.form.data.phone.label)}
          type="tel"
          placeholder={t(storesTranslation.myStore.form.data.phone.placeholder)}
          labelTooltip={merchant.isNursery ? undefined : 
            t(storesTranslation.myStore.form.data.phone.tooltip)}
          maxLength={255}
          disabled={!show}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('merchantWebUrl')}
          {...errors('merchantWebUrl')}
          id="onlineUrl"
          label={t(storesTranslation.myStore.form.data.url.label)}
          placeholder={t(storesTranslation.myStore.form.data.url.placeholder)}
          labelTooltip={merchant.isNursery ? undefined : 
            t(storesTranslation.myStore.form.data.url.tooltip)}
          legend={t(storesTranslation.myStore.form.data.url.legend)}
          maxLength={255}
          disabled={!show}  
        />
        {!merchant.isNursery ? (
        <OreSelect
          {...register('merchantCoockTypeId')}
          {...errors('merchantCoockTypeId')}
          id="foodType"
          label={t(storesTranslation.myStore.form.data.foodType.label)}
          required
          labelTooltip={t(storesTranslation.myStore.form.data.foodType.tooltip)}
          disabled={!show}
        >
          <OreSelectPlaceholder
            label={t(storesTranslation.myStore.form.data.foodType.placeholder)}
          />
          {foodTypes
            ? foodTypes.map(
                foodType =>
                  foodType.id && (
                    <option key={foodType.id} value={foodType.id}>
                      {foodType.name}
                    </option>
                  ),
              )
            : null}
        </OreSelect>): null}
      </OreStack>
      {!merchant.isNursery ? (
        <OreFormGroup
          label={t(storesTranslation.myStore.form.data.service.label)}
          required
          direction="row"
          {...errors('service')}
        >
          <OreCheckbox
            {...restOnline}
            {...errors('merchantIsOnlineStore')}
            label={t(storesTranslation.myStore.form.data.service.physical.label)}
            onChange={(e): void => {
              onChangeOnline(e)
              serviceError()
            }}
            disabled={!show}
          />
          <OreCheckbox
            {...restDelivery}
            {...errors('merchantHasHomeDelivery')}
            label={t(storesTranslation.myStore.form.data.service.delivery.label)}
            onChange={(e): void => {
              onChangeDelivery(e)
              serviceError()
            }}
            disabled={!show}
          />
          <OreCheckbox
            {...restTakeAway}
            {...errors('merchantHasLocalPickup')}
            label={t(storesTranslation.myStore.form.data.service.takeAway.label)}
            onChange={(e): void => {
              onChangeTakeAway(e)
              serviceError()
            }}
            disabled={!show}
          />
        </OreFormGroup>) : null}
    </OreStack>
  )
}
